import { QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { RedirectIfSignedIn, RequireAuth, RequireRole } from '@/components/RequireRole';
import { ScrollOnNavigate } from '@/components/ScrollOnNavigate';
import { ToastProvider } from '@/components/ui/toast';
import { AdminLayout } from '@/layouts/AdminLayout';
import { ClientLayout } from '@/layouts/ClientLayout';
import { EmployeeLayout } from '@/layouts/EmployeeLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { CareersPage } from '@/pages/public/CareersPage';
import { ContactPage } from '@/pages/public/ContactPage';
import { HomePage } from '@/pages/public/HomePage';
// `ServicesPage` is already taken by the admin rate-card screen. The public one
// is aliased rather than renamed — both are the right name in their own place.
import { ServicesPage as PublicServicesPage } from '@/pages/public/ServicesPage';
import { createQueryClient } from '@/lib/query-client';
import { LaunchPage } from '@/pages/LaunchPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { DashboardPage } from '@/pages/admin/DashboardPage';
import { PlaceholderPage } from '@/pages/admin/PlaceholderPage';
import { ApplicantDetailPage } from '@/pages/admin/ApplicantDetailPage';
import { ApplicantsPage } from '@/pages/admin/ApplicantsPage';
import { CalendarPage } from '@/pages/admin/CalendarPage';
import { ClientDetailPage } from '@/pages/admin/ClientDetailPage';
import { ConversationDetailPage } from '@/pages/admin/ConversationDetailPage';
import { ConversationsPage } from '@/pages/admin/ConversationsPage';
import { ExpensesPage } from '@/pages/admin/ExpensesPage';
import { InvoicesPage } from '@/pages/admin/InvoicesPage';
import { InvoiceDetailPage } from '@/pages/admin/InvoiceDetailPage';
import { ReportsPage } from '@/pages/admin/ReportsPage';
import { AnalyticsPage } from '@/pages/admin/AnalyticsPage';
import { TimesheetsPage } from '@/pages/admin/TimesheetsPage';
import { ClientsPage } from '@/pages/admin/ClientsPage';
import { EmployeeDetailPage } from '@/pages/admin/EmployeeDetailPage';
import { EmployeesPage } from '@/pages/admin/EmployeesPage';
import { JobDetailPage } from '@/pages/admin/JobDetailPage';
import { JobRequestsPage } from '@/pages/admin/JobRequestsPage';
import { JobsPage } from '@/pages/admin/JobsPage';
import { LeadsPage } from '@/pages/admin/LeadsPage';
import { NotificationEmailsPage } from '@/pages/admin/NotificationEmailsPage';
import { PositionsPage } from '@/pages/admin/PositionsPage';
import { PropertyCategoriesPage } from '@/pages/admin/PropertyCategoriesPage';
import { RecurringJobsPage } from '@/pages/admin/RecurringJobsPage';
import { CalendarSyncPage } from '@/pages/admin/CalendarSyncPage';
import { SchedulingPage } from '@/pages/admin/SchedulingPage';
import { ServicesPage } from '@/pages/admin/ServicesPage';
import { WorkerPage } from '@/pages/admin/WorkerPage';
import { ADMIN_PLACEHOLDER_ROUTES } from '@/pages/admin/adminRoutes';
import { EmployeeClientDetailPage } from '@/pages/employee/ClientDetailPage';
import { ClientsPage as EmployeeClientsPage } from '@/pages/employee/ClientsPage';
import { EmployeeDashboardPage } from '@/pages/employee/DashboardPage';
import { EmployeeExpensesPage } from '@/pages/employee/ExpensesPage';
import { EmployeeJobDetailPage } from '@/pages/employee/JobDetailPage';
import { MyJobsPage } from '@/pages/employee/MyJobsPage';
import { ProfilePage as EmployeeProfilePage } from '@/pages/employee/ProfilePage';
import { SchedulePage } from '@/pages/employee/SchedulePage';
import { CleanDetailPage } from '@/pages/client/CleanDetailPage';
import { ClientDashboardPage } from '@/pages/client/DashboardPage';
import { MyCleansPage } from '@/pages/client/MyCleansPage';
import { ClientInvoicesPage } from '@/pages/client/InvoicesPage';
import { ClientInvoiceDetailPage } from '@/pages/client/InvoiceDetailPage';
import { ProfilePage } from '@/pages/client/ProfilePage';
import { PropertiesPage } from '@/pages/client/PropertiesPage';
import { RequestCleaningPage } from '@/pages/client/RequestCleaningPage';
import { ChangePasswordPage } from '@/pages/auth/ChangePasswordPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { OFFICE_ROLES } from '@shared/schemas/user';
import { AdminAccountsPage } from '@/pages/admin/AdminAccountsPage';
import { SettingsPage } from '@/pages/admin/SettingsPage';

/*
 * Every route in the browser, registered by hand — the same rule the API
 * follows (CLAUDE.md §5). Reading this component is how you find out what the
 * app has, and a page that is not listed here is not reachable.
 *
 * `RequireRole` here keeps the wrong portal off the screen; it is **not** the
 * access control. Every endpoint behind these pages is guarded server-side in
 * src/server/middleware/auth.ts, which is what actually refuses a request.
 */

export default function App() {
  /*
   * One client for the life of the app, created in state rather than at module
   * scope. A module-level client is shared between every test in a file and
   * survives a hot reload with its cache intact, which makes both behave
   * differently from a real page load.
   */
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  return (
    <BrowserRouter>
      {/*
        Where the page sits after a route change — top, or an anchor, or back
        where the reader left it. Once here rather than per-layout, because all
        four surfaces scroll the window and all four had the same bug.
      */}
      <ScrollOnNavigate />

      <Routes>
        {/*
          **The public site owns `/`** — slice 8.4. It used to redirect to
          `/launch`, which was right while the only thing here was three
          portals and is wrong now: this is a business's marketing site, and a
          visitor who types the domain must land on it rather than be bounced
          through a sign-in resolver.

          `/launch` is unchanged and is still where sign-in lands. It is
          reached from the login form and from the topbar logo, never from `/`.
        */}
        <Route element={<PublicLayout />}>
          <Route index path="/" element={<HomePage />} />
          <Route path="/services" element={<PublicServicesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/careers" element={<CareersPage />} />

          {/*
            **The four auth screens live inside the public layout**, changed
            2026-08-07 after the owner pointed out that the login page was a
            dead end: no header, no nav, and nothing to click to get back to the
            site. It was its own bare page, so somebody who followed a Sign in
            link and changed their mind had the back button and nothing else.

            Most sites keep the nav on the login page and this now does too. The
            header also carries the real logo, which is the other half of what
            was wrong — see AuthCard.
          */}
          <Route
            path="/login"
            element={
              <RedirectIfSignedIn>
                <LoginPage />
              </RedirectIfSignedIn>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RedirectIfSignedIn>
                <ForgotPasswordPage />
              </RedirectIfSignedIn>
            }
          />
          {/*
            Not wrapped: a reset link is followed *because* the old password is
            unusable, and bouncing a signed-in user away from it would strand
            anyone resetting on a shared machine.
          */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            path="/change-password"
            element={
              <RequireAuth>
                <ChangePasswordPage />
              </RequireAuth>
            }
          />
        </Route>

        {/* Resolves the role and leaves. See LaunchPage — it always replaces. */}
        <Route path="/launch" element={<LaunchPage />} />

        {/* Both office roles. A super admin is an admin plus four permissions,
            not a different portal — business-rules.md §2. */}
        <Route
          path="/admin"
          element={
            <RequireRole allow={OFFICE_ROLES}>
              <AdminLayout />
            </RequireRole>
          }
        >
          <Route index element={<DashboardPage />} />

          {/* Built pages. Each one has a row in ADMIN_BUILT_ROUTES so the nav
              test can tell it apart from a placeholder. */}
          <Route path="property-categories" element={<PropertyCategoriesPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="positions" element={<PositionsPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          {/* Added 2026-08-06 — there had been no per-person page at all. */}
          <Route path="employees/:id" element={<EmployeeDetailPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:id" element={<ClientDetailPage />} />
          <Route path="conversations" element={<ConversationsPage />} />
          <Route path="conversations/:id" element={<ConversationDetailPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="jobs/:id" element={<JobDetailPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="job-requests" element={<JobRequestsPage />} />
          <Route path="scheduling" element={<SchedulingPage />} />
          <Route path="recurring-jobs" element={<RecurringJobsPage />} />
          {/* §11.6/§11.7 — iCal feeds from rental platforms; checkouts become
              turnover cleans on their own. */}
          <Route path="calendar-sync" element={<CalendarSyncPage />} />
          {/* §9.10 — the first screen to read `job_application`. The careers
              form has been live and public with nothing reading what it
              stored. */}
          <Route path="applicants" element={<ApplicantsPage />} />
          <Route path="applicants/:id" element={<ApplicantDetailPage />} />
          {/* §9.8 — the same story for the quote form. The selected lead lives
              in `?lead=`, so the pane survives a refresh and can be sent to
              somebody. */}
          <Route path="leads" element={<LeadsPage />} />
          {/* §10.1 — money out, with receipts. The office approves what
              cleaners submit and records the business costs nobody is owed
              for. */}
          <Route path="expenses" element={<ExpensesPage />} />

          {/* Phase 12. The builder lives on the detail route; the list is the ledger. */}
          <Route path="invoices" element={<InvoicesPage />} />
          {/* Super-admin-only on the server; an ordinary admin who reaches the
              URL gets an error from every call rather than a working screen. */}
          <Route path="admins" element={<AdminAccountsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="invoices/:id" element={<InvoiceDetailPage />} />
          {/* §10.2 — payroll hours under the rule Utah actually follows.
              The prototype computes overtime daily, which is California's. */}
          <Route path="timesheets" element={<TimesheetsPage />} />
          {/* §10.4 and §10.5 — the four report types, and the four KPI cards
              the prototype leaves showing a dash forever (P1). */}
          <Route path="reports" element={<ReportsPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="worker" element={<WorkerPage />} />
          {/* §11.3, moved into phase 8 — it is the only screen that can put a
              row in `notification_email_recipient`, and 8.7's exit criterion
              depends on one existing. */}
          <Route path="notification-emails" element={<NotificationEmailsPage />} />

          {/*
            One route per sidebar item, from the same list the sidebar reads.
            Every nav item leads somewhere from the moment the shell exists, and
            each destination names the phase that builds it — a link that 404s
            makes a finished shell look broken.
          */}
          {ADMIN_PLACEHOLDER_ROUTES.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <PlaceholderPage title={route.title} phase={route.phase} summary={route.summary} />
              }
            />
          ))}
        </Route>

        <Route
          path="/employee"
          element={
            <RequireRole allow={['employee']}>
              <EmployeeLayout />
            </RequireRole>
          }
        >
          <Route index element={<EmployeeDashboardPage />} />

          {/* One route per item in EMPLOYEE_NAV, so no link in the shell 404s.
              The placeholders name the slice that replaces them and are gone by
              the end of the phase. */}
          <Route path="jobs" element={<MyJobsPage />} />
          <Route path="jobs/:id" element={<EmployeeJobDetailPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="clients" element={<EmployeeClientsPage />} />
          <Route path="clients/:id" element={<EmployeeClientDetailPage />} />
          <Route path="expenses" element={<EmployeeExpensesPage />} />
          <Route path="profile" element={<EmployeeProfilePage />} />
        </Route>

        <Route
          path="/portal"
          element={
            <RequireRole allow={['client']}>
              <ClientLayout />
            </RequireRole>
          }
        >
          <Route index element={<ClientDashboardPage />} />

          {/* One route per item in CLIENT_NAV, so no link in the shell 404s. */}
          <Route path="cleans" element={<MyCleansPage />} />
          <Route path="cleans/:id" element={<CleanDetailPage />} />
          {/* Phase 12.5 — issued invoices only; drafts are the office's. */}
          <Route path="invoices" element={<ClientInvoicesPage />} />
          <Route path="invoices/:id" element={<ClientInvoiceDetailPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="request" element={<RequestCleaningPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
